import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import { isEmpty } from 'lodash-es';
import { connect } from 'react-redux';
import { addListeners, IListeners } from 'decorators/addListeners';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import { WithRouterProps } from 'decorators/withRouter';
import apiPayouts from 'api/payouts';
import { StoreProps } from 'store';
import showNotification from 'components/ui/notification/showNotification';
import BatchPreview from 'components/formFields/components/massPayment/components/batchPreview';
import SubmittedForm from 'components/formFields/components/submittedForm';
import MassPayment from 'components/formFields/components/massPayment';
import { MassPaymentGeneralColumn } from 'components/formFields/components/massPayment/components/MassPaymentGeneralColumn';
import { MassPaymentAdditionalColumn } from 'components/formFields/components/massPayment/components/MassPaymentAdditionalColumn';
import { MassPaymentFooter } from 'components/formFields/components/massPayment/components/MassPaymentFooter';
import RpcTypes from 'constants/rpcTypes';
import { AnyObject } from 'types/Common';
import BatchFile from 'types/BatchFile';
import { RouteLeavingGuard } from 'components/routeLeavingGuard';

const POLLING_INTERVAL = 10000;
const STATUS_AWAITING_UPLOAD_COMPLETE = 0;

interface OwnProps {
  type: string;
  backUrl: string;
}

interface ConnectedProps {
  isBulkPayoutsExpanded: boolean;
}

type Props = OwnProps &
  ConnectedProps &
  StoreProps &
  IntlProps &
  WithRouterProps;

interface State {
  file: File | null;
  batch: BatchFile | null;
  isFilePreview: boolean;
  isFileUploading: boolean;
  isBatchPreview: boolean;
  isBatchDeleting: boolean;
  isBatchUploading: boolean;
  isSuccessRequest: boolean;
  validationErrors: AnyObject;
  idsToCheck: Record<string, boolean>;
  isFieldsDirty: boolean;
}

@addListeners([
  RpcTypes.BulkPayouts_Confirm,
  RpcTypes.PaymentOperationBatch_Uploaded,
  RpcTypes.BulkPayouts_Preview,
])
class MassRequestContainer
  extends PureComponent<Props, State>
  implements IListeners
{
  private currentOperationBatchId;
  private pollingTimerId;

  constructor(props: Props) {
    super(props);

    this.state = {
      file: null,
      batch: null,
      isBatchPreview: false,
      isFilePreview: false,
      isFileUploading: false,
      isBatchUploading: false,
      isBatchDeleting: false,
      isSuccessRequest: false,
      validationErrors: {},
      idsToCheck: {},
      isFieldsDirty: false,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.type !== this.props.type) {
      this.resetState();
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  render() {
    const { isFieldsDirty } = this.state;
    const { type } = this.props;

    return (
      <>
        <MassPayment
          id={type}
          isLoading={this.state.isBatchUploading}
          isSuccess={this.state.isSuccessRequest}
          isFilePreview={this.state.isBatchPreview}
          batch={this.state.batch}
          previewText={`payouts.request.mass.${type}.infoText`}
          renderGeneralColumn={this.renderGeneralColumn}
          renderAdditionalColumn={this.renderAdditionalColumn}
          renderFooter={this.renderFooter}
          renderBatch={this.renderBatchPreview}
          renderSubmitted={this.renderSubmitted}
        />

        <RouteLeavingGuard blockWhen={isFieldsDirty} />
      </>
    );
  }

  renderGeneralColumn = () => {
    const { type, isBulkPayoutsExpanded, getTranslate } = this.props;

    return (
      <MassPaymentGeneralColumn
        title={getTranslate(
          `payouts.request.mass.${type}.loading.templatesTitle`
        )}
        templatesText={getTranslate(
          'payouts.request.mass.readymadeTemplate.label'
        )}
        infoText={
          type === 'payout' && isBulkPayoutsExpanded
            ? getTranslate(
                'payouts.request.mass.readymadeTemplate.info.tooltip'
              )
            : ''
        }
        templates={[
          {
            title: getTranslate('payouts.request.mass.loading.templateCard'),
            url: `files/mass_${type}_cards.csv`,
            isAvailable: true,
          },
          {
            title: getTranslate(
              'payouts.request.mass.loading.templateCardExpanded'
            ),
            url: `files/mass_${type}_cards_expanded.csv`,
            isAvailable: type === 'payout' && isBulkPayoutsExpanded,
          },
          {
            title: getTranslate('payouts.request.mass.loading.templateAPS'),
            url: `files/mass_${type}_aps.csv`,
            isAvailable: true,
          },
        ]}
      />
    );
  };

  renderAdditionalColumn = () => {
    const { getTranslate } = this.props;

    return (
      <MassPaymentAdditionalColumn
        title={getTranslate('remittance.addPayout.mass.attachFiles.label')}
        batch={this.state.batch}
        isFilePreview={this.state.isFilePreview}
        file={this.state.file}
        isFileLoading={this.state.isFileUploading}
        onChangeFile={this.validateFile}
        onChangeField={this.changeField}
        onDeleteFile={this.resetState}
        error={this.state.validationErrors?.file}
        switcherText={getTranslate('payouts.request.mass.loading.filePreview')}
      />
    );
  };

  renderFooter = () => {
    const { type, getTranslate, backUrl } = this.props;

    return (
      <MassPaymentFooter
        id={type}
        isFileLoading={this.state.isBatchUploading}
        backUrl={backUrl}
        isFilePreview={this.state.isFilePreview}
        isBatchPreview={this.state.isBatchPreview}
        backButtonText={getTranslate('payouts.request.backBtn')}
        createButtonText={this.setButtonText()}
        canCreate={!this.checkValidation()}
        onCreate={this.setButtonHandler()}
      />
    );
  };

  renderBatchPreview = () => {
    if (
      !this.state.batch ||
      !this.state.idsToCheck ||
      !this.state.isBatchPreview
    )
      return null;

    return (
      <BatchPreview
        type={this.props.type}
        batch={this.state.batch}
        file={this.state.file}
        isDeleting={this.state.isBatchDeleting}
        onDeleteRow={() => this.deletePayoutFromBatch()}
        onSelectToDelete={(id) => this.checkPayoutsToDeleteFromBatch(id)}
        idsToCheck={this.state.idsToCheck}
      />
    );
  };

  renderSubmitted = () => {
    const { type, backUrl } = this.props;
    return (
      <SubmittedForm
        id={type}
        text='payouts.request.mass.prevention.text'
        status='success'
        secondaryButtonText='payouts.request.backBtn'
        onSecondaryButtonLink={backUrl}
        primaryButtonText='payouts.request.newRequestBtn'
        onPrimaryButtonClick={this.resetState}
      />
    );
  };

  uploadFile = async () => {
    const { file } = this.state;
    const { type } = this.props;

    this.setState({ isFileUploading: true });
    const request =
      type === 'cancel' || type === 'capture'
        ? apiPayouts.uploadFileCaptureCancel
        : apiPayouts.uploadFile;
    try {
      const batch = await request(file, type);
      this.startPolling();
      if (batch) {
        this.currentOperationBatchId = batch.paymentOperationBatchId;
      }
    } catch (error: any) {
      if (error.payload) {
        this.setState({
          validationErrors: error.payload.validationErrors,
          isFileUploading: false,
        });
      } else {
        this.setState({
          isFileUploading: false,
        });
      }
    }
  };

  setIdsChecks = (batch: BatchFile | null) => {
    if (!batch) return;

    const { rows } = batch;
    const rowsToCheck = rows.reduce((resultIds, item) => {
      return { ...resultIds, [item.id]: false };
    }, {});

    this.setState({ idsToCheck: rowsToCheck });
  };

  checkPayoutsToDeleteFromBatch = (data: number | string) => {
    const { idsToCheck, batch } = this.state;
    if (!batch) return;

    let newIdsToCheck;
    if (typeof data === 'number') {
      newIdsToCheck = { ...idsToCheck, [data]: !idsToCheck[data] };
    } else {
      newIdsToCheck = this.toggleAll(data);
    }
    this.setState({ idsToCheck: newIdsToCheck });
  };

  toggleAll = (data) => {
    const { idsToCheck } = this.state;

    if (data === 'checkAll') {
      return Object.keys(idsToCheck).reduce((acc, item) => {
        return { ...acc, [item]: true };
      }, {});
    }
    return Object.keys(idsToCheck).reduce((acc, item) => {
      return { ...acc, [item]: false };
    }, {});
  };

  deletePayoutFromBatch = async () => {
    const {
      batch: { paymentOperationBatchId },
      idsToCheck,
    }: any = this.state;
    const idsToDelete = Object.keys(idsToCheck).filter(
      (item) => idsToCheck[item]
    );
    this.setState({ isBatchDeleting: true });
    try {
      const { batch } = await apiPayouts.deletePayoutsFromBatch({
        payoutQueueIds: [...idsToDelete],
        paymentOperationBatchId,
      });
      this.setState({ batch });
      this.setIdsChecks(batch);
    } finally {
      this.setState({ isBatchDeleting: false });
    }
  };

  sendBatch = async () => {
    const { getTranslate } = this.props;
    const { batch } = this.state;

    if (!batch) return;

    this.setState({ isBatchUploading: true });

    try {
      await apiPayouts.sendBatch(batch.paymentOperationBatchId);
    } catch ({ payload }: any) {
      const { validationErrors } = payload;
      if (validationErrors && Object.values(validationErrors).length) {
        showNotification({
          status: 'error',
          content: getTranslate(Object.values(validationErrors)[0] as string),
        });
      }
    } finally {
      this.setState({
        isBatchUploading: false,
      });
    }
  };

  startPolling = () => {
    this.pollingTimerId = setInterval(() => {
      return apiPayouts.getBatchPreview(this.currentOperationBatchId);
    }, POLLING_INTERVAL);
  };

  stopPolling = () => {
    clearInterval(this.pollingTimerId);
  };

  validateFile = async (file) => {
    await this.resetState();
    await new Promise<void>((resolve) => this.setState({ file }, resolve));
    await this.uploadFile();
    this.setState({
      isFieldsDirty: true,
    });
  };

  sendData = async () => {
    await this.sendBatch();
  };

  goToFilePreview = () => {
    this.setState({
      isBatchPreview: true,
    });
  };

  checkValidation = () => {
    const {
      batch,
      validationErrors,
      isFileUploading,
      isBatchUploading,
      isFilePreview,
      isBatchPreview,
    } = this.state;

    const checkErrors = isEmpty(validationErrors)
      ? !batch
      : !(batch && isFilePreview);

    const batchErrors = isBatchPreview ? batch && batch.isValid : true;

    return (
      isBatchUploading ||
      isFileUploading ||
      !batch ||
      checkErrors ||
      !batchErrors
    );
  };

  changeField = (field: string, value: any) => {
    this.setState((state) => {
      return {
        ...state,
        [field]: value,
      };
    });
  };

  resetState = () => {
    return new Promise<void>((resolve) =>
      this.setState(
        (prevState) => ({
          ...prevState,
          validationErrors: {},
          file: null,
          batch: null,
          isFilePreview: false,
          isBatchPreview: false,
          isSuccessRequest: false,
          idsToCheck: {},
          isFieldsDirty: false,
        }),
        resolve
      )
    );
  };

  setButtonText = () => {
    const { isFilePreview, isBatchPreview } = this.state;
    const { getTranslate } = this.props;

    if (isFilePreview && !isBatchPreview) {
      return getTranslate('payouts.request.detailsBtn');
    }
    return getTranslate('payouts.request.sendRequestBtn');
  };

  setButtonHandler = () => {
    const { isFilePreview, isBatchPreview } = this.state;

    if (isFilePreview && !isBatchPreview) {
      return this.goToFilePreview;
    }
    return () => this.sendData();
  };

  onEvent = ({ name, data }) => {
    const { status } = data.rpc;
    const { payload } = data;

    if (
      !this.currentOperationBatchId &&
      payload.paymentOperationBatchId !== this.currentOperationBatchId
    )
      return;
    switch (name) {
      case RpcTypes.BulkPayouts_Confirm:
        if (status === 'success') {
          this.setState({
            isSuccessRequest: true,
            isFieldsDirty: false,
          });
        }
        break;
      case RpcTypes.BulkPayouts_Preview: {
        const { batch, validationErrors } = payload;

        if (
          !validationErrors &&
          batch.status !== STATUS_AWAITING_UPLOAD_COMPLETE
        )
          return;

        this.setState({
          validationErrors: validationErrors || {},
          batch: batch || null,
          isFileUploading: false,
        });
        batch && this.setIdsChecks(batch);
        this.stopPolling();
        break;
      }

      case RpcTypes.PaymentOperationBatch_Uploaded:
        return apiPayouts.getBatchPreview(this.currentOperationBatchId);
    }
  };
}

const mapStateToProps = (state): ConnectedProps => ({
  isBulkPayoutsExpanded: state.settings.isBulkPayoutsExpanded,
});

export default withRouter(
  connect(mapStateToProps)(addTranslation(MassRequestContainer))
);
